import React from "react";
import { Users, Volume2, VolumeX, Hash, Building2 } from "lucide-react";
import { getFullName } from "@/lib/userUtils";
import { resolvePresence } from "@/lib/presence";
import { useDmSocket } from "../../scripts/DmSocketContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { plainExcerpt } from "@/lib/markdownPlain";

export default function DmConversationList({
  conversations,
  loading,
  selectedId,
  onSelect,
  onToggleMute,
  className,
  emptyMessage = "No conversations yet. Start a new chat or message someone from Members.",
}) {
  const { presenceByUserId } = useDmSocket();

  if (loading) {
    return (
      <div className={cn("p-4 text-sm text-muted-foreground", className)}>
        Loading conversations…
      </div>
    );
  }

  if (!conversations?.length) {
    return (
      <div className={cn("p-4 text-sm text-muted-foreground", className)}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <ul className={cn("divide-y divide-border", className)}>
      {conversations.map((c) => {
        const isPartner = c.type === "partner";
        const active = isPartner
          ? String(c.conversation_id) === String(selectedId)
          : Number(c.conversation_id) === Number(selectedId);
        const isSelf = !!c.is_self;
        const isGroup = c.type === "group";
        const isChannel = c.type === "channel";
        const title = isSelf
          ? "You"
          : isPartner
            ? c.title || "Partner channel"
            : isGroup || isChannel
              ? c.title || (isChannel ? "Channel" : "Group")
              : getFullName(c.peer);
        const presence =
          !isSelf && !isGroup && !isChannel && !isPartner
            ? resolvePresence(c.peer, presenceByUserId)
            : null;
        const preview = isPartner
          ? c.subtitle ||
            (c.partner_org?.name
              ? `with ${c.partner_org.name}`
              : "External partner space")
          : c.last_message?.kind === "reaction"
            ? c.last_message.content
            : c.last_message?.deleted
              ? "Message deleted"
              : c.last_message?.content
                ? plainExcerpt(c.last_message.content, 120)
                : c.last_message?.has_attachment
                  ? "Shared a post"
                  : "No messages yet";
        const partnerAvatar = isPartner
          ? resolveAvatarUrl(c.partner_org?.avatar_url)
          : null;
        const showUnread = !c.muted && c.unread_count > 0;
        const canMute =
          !isSelf && !isPartner && typeof onToggleMute === "function";
        return (
          <li key={c.conversation_id} className="group/row">
            <div
              className={cn(
                "w-full flex items-start gap-3 px-3 py-3 text-left hover:bg-muted/50 transition-colors",
                active && "bg-muted",
                c.muted && "opacity-75"
              )}
            >
              <button
                type="button"
                onClick={() => onSelect?.(c)}
                className="flex flex-1 min-w-0 items-start gap-3 text-left"
              >
                <div className="relative shrink-0">
                  {isSelf || isGroup || isChannel || isPartner ? (
                    <Avatar className="h-10 w-10">
                      {isPartner && partnerAvatar ? (
                        <AvatarImage
                          src={partnerAvatar}
                          alt={c.partner_org?.name || "Partner organization"}
                        />
                      ) : null}
                      <AvatarFallback className="text-sm">
                        {isSelf ? (
                          "Y"
                        ) : isPartner ? (
                          <Building2 className="h-4 w-4" />
                        ) : isGroup ? (
                          <Users className="h-4 w-4" />
                        ) : (
                          <Hash className="h-4 w-4" />
                        )}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <OrgMemberAvatar
                      user={c.peer}
                      className="h-10 w-10"
                      fallbackClassName="text-sm font-semibold"
                    />
                  )}
                  {presence && (
                    <span
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
                        presence.dot
                      )}
                      title={presence.label}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{title}</span>
                    {presence && (
                      <span
                        className="text-[10px] text-muted-foreground shrink-0 hidden sm:inline"
                        title={presence.label}
                      >
                        {presence.shortLabel}
                      </span>
                    )}
                    {showUnread && (
                      <Badge
                        variant="destructive"
                        className="h-5 min-w-5 px-1.5 text-[10px] shrink-0 ml-auto"
                      >
                        {c.unread_count > 99 ? "99+" : c.unread_count}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                    {isGroup && !c.last_message
                      ? `${c.member_count || c.members?.length || 0} members`
                      : preview}
                  </p>
                </div>
              </button>
              {canMute && (
                <button
                  type="button"
                  title={c.muted ? "Unmute" : "Mute"}
                  aria-label={c.muted ? "Unmute conversation" : "Mute conversation"}
                  className={cn(
                    "shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-background/80 hover:text-foreground mt-0.5 md:h-7 md:w-7",
                    c.muted
                      ? "opacity-100"
                      : "opacity-100 md:opacity-0 md:group-hover/row:opacity-100 focus-visible:opacity-100"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleMute(c, !c.muted);
                  }}
                >
                  {c.muted ? (
                    <VolumeX className="h-3.5 w-3.5" />
                  ) : (
                    <Volume2 className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
